/**
 * User Metadata Collection Utility
 *
 * Collects comprehensive user context information including:
 * - Geolocation (if permitted)
 * - Timezone and locale
 * - Device and browser information
 * - Screen resolution
 * - Network information
 * - Referrer and page context
 *
 * This metadata is sent with each chat request to provide
 * context-aware and personalized responses from the LLM.
 */

export interface UserMetadata {
  // Location information
  location?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    city?: string;
    country?: string;
    timezone?: string;
  };

  // Time and locale
  timezone: string;
  timezoneOffset: number; // Minutes from UTC
  locale: string;
  language: string;
  languages: readonly string[];

  // Device information
  device: {
    type: "mobile" | "tablet" | "desktop" | "unknown";
    platform: string;
    userAgent: string;
    vendor: string;
  };

  // Browser information
  browser: {
    name: string;
    version: string;
    engine: string;
  };

  // Screen information
  screen: {
    width: number;
    height: number;
    colorDepth: number;
    pixelRatio: number;
    orientation?: "portrait" | "landscape";
  };

  // Network information (if available)
  network?: {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  };

  // Session context
  session: {
    referrer: string;
    currentUrl: string;
    timestamp: string;
  };
}

/**
 * Detect device type based on screen width and user agent
 */
function getDeviceType(): "mobile" | "tablet" | "desktop" | "unknown" {
  if (typeof window === "undefined") return "unknown";

  const ua = navigator.userAgent.toLowerCase();
  const width = window.screen.width;

  // Check user agent first for more accurate detection
  if (/mobile|android|iphone|ipod|blackberry|windows phone/i.test(ua)) {
    return width >= 600 ? "tablet" : "mobile";
  }

  if (/ipad|tablet|playbook|silk/i.test(ua)) {
    return "tablet";
  }

  // Fallback to screen width
  if (width < 768) return "mobile";
  if (width < 1024) return "tablet";
  return "desktop";
}

/**
 * Parse user agent to extract browser information
 */
function getBrowserInfo(): {
  name: string;
  version: string;
  engine: string;
} {
  if (typeof window === "undefined") {
    return { name: "unknown", version: "unknown", engine: "unknown" };
  }

  const ua = navigator.userAgent;

  // Detect browser name and version
  let name = "unknown";
  let version = "unknown";
  let engine = "unknown";

  if (ua.includes("Firefox/")) {
    name = "Firefox";
    version = ua.match(/Firefox\/([0-9.]+)/)?.[1] || "unknown";
    engine = "Gecko";
  } else if (ua.includes("Edg/")) {
    name = "Edge";
    version = ua.match(/Edg\/([0-9.]+)/)?.[1] || "unknown";
    engine = "Blink";
  } else if (ua.includes("Chrome/")) {
    name = "Chrome";
    version = ua.match(/Chrome\/([0-9.]+)/)?.[1] || "unknown";
    engine = "Blink";
  } else if (ua.includes("Safari/")) {
    name = "Safari";
    version = ua.match(/Version\/([0-9.]+)/)?.[1] || "unknown";
    engine = "WebKit";
  } else if (ua.includes("Opera/") || ua.includes("OPR/")) {
    name = "Opera";
    version = ua.match(/(?:Opera|OPR)\/([0-9.]+)/)?.[1] || "unknown";
    engine = "Blink";
  }

  return { name, version, engine };
}

/**
 * Get network information if available (experimental API)
 */
function getNetworkInfo() {
  if (typeof window === "undefined" || !("connection" in navigator)) {
    return undefined;
  }

  const connection = (navigator as any).connection;
  if (!connection) return undefined;

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

/**
 * Get geolocation if user grants permission
 * Returns a promise that resolves with location data or undefined
 */
async function getGeolocation(): Promise<
  | {
      latitude: number;
      longitude: number;
      accuracy: number;
    }
  | undefined
> {
  if (typeof window === "undefined" || !("geolocation" in navigator)) {
    return undefined;
  }

  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      position => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      () => {
        // User denied or error occurred - silently fail
        resolve(undefined);
      },
      {
        timeout: 5000,
        maximumAge: 300000, // 5 minutes cache
        enableHighAccuracy: false, // Faster, less accurate
      }
    );
  });
}

/**
 * Collect all user metadata
 *
 * @param includeGeolocation - Whether to attempt geolocation (requires user permission)
 * @returns Comprehensive user metadata object
 */
export async function collectUserMetadata(
  includeGeolocation: boolean = false
): Promise<UserMetadata> {
  // Server-side rendering fallback
  if (typeof window === "undefined") {
    console.log("[User Metadata] Running on server, returning server defaults");
    return {
      timezone: "UTC",
      timezoneOffset: 0,
      locale: "en",
      language: "en",
      languages: ["en"],
      device: {
        type: "unknown",
        platform: "server",
        userAgent: "server",
        vendor: "server",
      },
      browser: {
        name: "unknown",
        version: "unknown",
        engine: "unknown",
      },
      screen: {
        width: 0,
        height: 0,
        colorDepth: 0,
        pixelRatio: 1,
      },
      session: {
        referrer: "",
        currentUrl: "",
        timestamp: new Date().toISOString(),
      },
    };
  }

  console.log("[User Metadata] Starting collection...");

  try {
    // Collect geolocation if requested
    let locationData: UserMetadata["location"];
    if (includeGeolocation) {
      console.log("[User Metadata] Collecting geolocation...");
      const coords = await getGeolocation();
      if (coords) {
        locationData = {
          ...coords,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        console.log("[User Metadata] Geolocation collected:", locationData);
      } else {
        console.log("[User Metadata] No geolocation available");
      }
    }

    console.log("[User Metadata] Collecting browser info...");

    // Collect all metadata
    const metadata: UserMetadata = {
      // Location (if available)
      ...(locationData && { location: locationData }),

      // Time and locale
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      locale: navigator.language,
      language: navigator.language.split("-")[0],
      languages: navigator.languages,

      // Device
      device: {
        type: getDeviceType(),
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
      },

      // Browser
      browser: getBrowserInfo(),

      // Screen
      screen: {
        width: window.screen.width,
        height: window.screen.height,
        colorDepth: window.screen.colorDepth,
        pixelRatio: window.devicePixelRatio,
        orientation:
          window.screen.width > window.screen.height ? "landscape" : "portrait",
      },

      // Network (if available)
      network: getNetworkInfo(),

      // Session context
      session: {
        referrer: document.referrer,
        currentUrl: window.location.href,
        timestamp: new Date().toISOString(),
      },
    };

    console.log("[User Metadata] Collection completed successfully");
    return metadata;
  } catch (error) {
    console.error("[User Metadata] Error during collection:", error);
    // Return minimal fallback data
    return {
      timezone: "UTC",
      timezoneOffset: 0,
      locale: "en",
      language: "en",
      languages: ["en"],
      device: {
        type: "unknown",
        platform: "unknown",
        userAgent: "unknown",
        vendor: "unknown",
      },
      browser: {
        name: "unknown",
        version: "unknown",
        engine: "unknown",
      },
      screen: {
        width: 0,
        height: 0,
        colorDepth: 0,
        pixelRatio: 1,
      },
      session: {
        referrer: "",
        currentUrl: "",
        timestamp: new Date().toISOString(),
      },
    };
  }
}

/**
 * Collect lightweight user metadata (without geolocation)
 * This is faster and doesn't require user permission
 */
export async function collectLightweightUserMetadata(): Promise<UserMetadata> {
  console.log("[User Metadata] collectLightweightUserMetadata called");
  try {
    const result = await collectUserMetadata(false);
    console.log(
      "[User Metadata] collectLightweightUserMetadata returning:",
      result
    );
    return result;
  } catch (error) {
    console.error(
      "[User Metadata] collectLightweightUserMetadata failed:",
      error
    );
    // Return minimal working metadata
    return {
      timezone: "UTC",
      timezoneOffset: 0,
      locale: "en",
      language: "en",
      languages: ["en"],
      device: {
        type: "desktop",
        platform: "unknown",
        userAgent: "unknown",
        vendor: "unknown",
      },
      browser: {
        name: "unknown",
        version: "unknown",
        engine: "unknown",
      },
      screen: {
        width: 1920,
        height: 1080,
        colorDepth: 24,
        pixelRatio: 1,
      },
      session: {
        referrer: "",
        currentUrl: "unknown",
        timestamp: new Date().toISOString(),
      },
    };
  }
}

/**
 * Serialize metadata for logging/debugging
 */
export function serializeMetadata(metadata: UserMetadata): string {
  return JSON.stringify(metadata, null, 2);
}
